import { View, Text, TextInput, Pressable } from 'react-native';
import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { getMarylandLocalityLabel } from '@perdiem/trpc/maryland-local-tax';
import { trpc } from '../../lib/trpc';
import { trackOnboardingStepCompleted } from '../../lib/product-events';
import {
  OnboardingShell,
  Button,
  Card,
  StateSelect,
  MarylandLocalitySelect,
  WebHead,
} from '../../components';

const FILING_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
] as const;

export default function Step1AboutYou() {
  const router = useRouter();
  const pathname = usePathname();
  const utils = trpc.useUtils();
  const profilesQ = trpc.profiles.list.useQuery();
  const updateProfileMutation = trpc.profiles.update.useMutation();
  const advanceMutation = trpc.households.updateOnboardingStep.useMutation();

  const primaryProfile = profilesQ.data?.find((p) => p.type === 'primary') ?? profilesQ.data?.[0];
  const [hydratedProfileId, setHydratedProfileId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [state, setState] = useState('');
  const [taxLocality, setTaxLocality] = useState('');
  const [filingStatus, setFilingStatus] = useState<string>('single');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!primaryProfile || hydratedProfileId === primaryProfile.id) return;
    setFirstName(primaryProfile.firstName ?? '');
    setBirthYear(primaryProfile.birthYear?.toString() ?? '');
    setState(primaryProfile.state ?? '');
    setTaxLocality(primaryProfile.taxLocality ?? '');
    setFilingStatus(primaryProfile.filingStatus ?? 'single');
    setHydratedProfileId(primaryProfile.id);
  }, [hydratedProfileId, primaryProfile]);

  const handleContinue = async () => {
    setError(null);
    if (!primaryProfile) {
      setError('Could not find your profile. Try refreshing the page.');
      return;
    }

    const trimmedFirstName = firstName.trim();
    if (!trimmedFirstName) {
      setError('Please enter your first name.');
      return;
    }

    const birth = parseInt(birthYear, 10);
    if (!birth || birth < 1920 || birth > 2010) {
      setError('Please enter a valid birth year (1920-2010).');
      return;
    }

    if (state === 'MD' && !taxLocality) {
      setError('Please select your Maryland county or Baltimore City.');
      return;
    }

    try {
      await updateProfileMutation.mutateAsync({
        id: primaryProfile.id,
        firstName: trimmedFirstName,
        birthYear: birth,
        state: state || undefined,
        taxLocality: state === 'MD' ? taxLocality : undefined,
        filingStatus: filingStatus as 'single' | 'married_filing_jointly',
      });
      await Promise.all([
        utils.profiles.list.invalidate(),
        profilesQ.refetch(),
      ]);
      await advanceMutation.mutateAsync({ step: 2 });
      // The trial route uses the stored onboarding step to decide whether to
      // resume setup or show the activation gate. Refresh this query before
      // navigating so it cannot read the pre-mutation step-1 cache and bounce
      // the user back to About You.
      await utils.households.get.invalidate();
      trackOnboardingStepCompleted({
        route: pathname,
        stepNumber: 1,
        nextStepNumber: 2,
        completionKind: 'continue',
        metadata: {
          stateProvided: state.length > 0,
          filingStatus,
          ...(state === 'MD' && taxLocality
            ? { marylandLocality: getMarylandLocalityLabel(taxLocality) ?? taxLocality }
            : {}),
        },
      });
      router.replace('/(onboarding)/step-2-connect' as any);
    } catch {
      setError('Could not save your profile. Please try again.');
    }
  };

  return (
    <OnboardingShell
      step={0}
      title="About You"
      subtitle="A few basics help Per Diem estimate taxes, retirement timing, Social Security, and age-based planning milestones."
      showBack={false}
      footer={
        <View className="gap-3">
          {error && (
            <View className="rounded-xl bg-red-50 px-4 py-3">
              <Text className="text-sm text-red-600">{error}</Text>
            </View>
          )}
          <Button
            testID="onboarding-about-continue-button"
            accessibilityLabel="Continue from about you"
            onPress={handleContinue}
            loading={updateProfileMutation.isPending || advanceMutation.isPending}
          >
            Continue →
          </Button>
        </View>
      }
    >
      <WebHead><title>Step 1: About You – Per Diem</title></WebHead>
      <View className="gap-4">
        <Card>
          <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">
            Your profile
          </Text>
          <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">First Name</Text>
          <TextInput
            testID="onboarding-about-first-name-input"
            accessibilityLabel="First name"
            value={firstName}
            onChangeText={(v) => { setFirstName(v); setError(null); }}
            placeholder="What should we call you?"
            autoCapitalize="words"
            autoCorrect={false}
            className="border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200 mb-4"
          />
          <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">Birth Year</Text>
          <TextInput
            testID="onboarding-about-birth-year-input"
            accessibilityLabel="Birth year"
            value={birthYear}
            onChangeText={(v) => { setBirthYear(v); setError(null); }}
            placeholder="e.g. 1985"
            keyboardType="numeric"
            className="border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200 mb-4"
          />
          <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">State</Text>
          <StateSelect
            value={state}
            onChange={(next) => {
              setState(next);
              if (next !== 'MD') setTaxLocality('');
              setError(null);
            }}
          />
          {state === 'MD' && (
            <View className="mt-4">
              <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Maryland County or Baltimore City
              </Text>
              <MarylandLocalitySelect
                value={taxLocality}
                onChange={(next) => {
                  setTaxLocality(next);
                  setError(null);
                }}
              />
              <Text className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Used for local Maryland income tax estimates.
              </Text>
            </View>
          )}
        </Card>

        <Card>
          <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
            Tax filing status
          </Text>
          <Text className="text-xs text-gray-400 mb-4">
            You can refine household members later. This gives tax estimates a better starting point.
          </Text>
          <View className="gap-2">
            {FILING_OPTIONS.map((option) => (
              <Pressable
                key={option.value}
                onPress={() => {
                  setFilingStatus(option.value);
                  setError(null);
                }}
                className={`rounded-xl border px-4 py-3 ${
                  filingStatus === option.value
                    ? 'border-green-600 bg-green-50 dark:bg-green-900/30'
                    : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
                }`}
              >
                <Text
                  className={`text-sm font-semibold ${
                    filingStatus === option.value
                      ? 'text-green-800 dark:text-green-300'
                      : 'text-gray-800 dark:text-gray-200'
                  }`}
                >
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </Card>
      </View>
    </OnboardingShell>
  );
}
